import { useRef, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { FieldValue, QueryRule } from '../../../../../types/QueryBuilder';
import { useCriteriaValueSearch } from '../../../../../hooks/QueryBuilder/useCriteriaValueSearch';

interface RemoteSearchEditorProps {
    criteria: string;
    value: QueryRule['value'];
    onChange: (value: string[]) => void;
    placeholder: string;
}

const toCodes = (value: QueryRule['value']): string[] => {
    if (value === null || value === undefined || value === '') return [];
    return Array.isArray(value) ? value : String(value).split(',').map(v => v.trim()).filter(Boolean);
};

function RemoteSearchEditor({ criteria, value, onChange, placeholder }: RemoteSearchEditorProps) {
    const [input, setInput] = useState('');
    const { options, loading, error } = useCriteriaValueSearch(criteria, input);
    const labels = useRef(new Map<string, string>());
    options.forEach(o => labels.current.set(o.name, o.label));

    const selected: FieldValue[] = toCodes(value).map(code => ({ name: code, label: labels.current.get(code) ?? code }));

    return (
        <Autocomplete<FieldValue, true>
            multiple
            size="small"
            fullWidth
            filterSelectedOptions
            options={options}
            value={selected}
            loading={loading}
            inputValue={input}
            onInputChange={(_, v, reason) => reason !== 'reset' && setInput(v)}
            filterOptions={opts => opts}
            getOptionLabel={o => `${o.label} (${o.name})`}
            isOptionEqualToValue={(a, b) => a.name === b.name}
            onChange={(_, next) => onChange(next.map(o => o.name))}
            noOptionsText={error ? 'Recherche indisponible' : input.trim() ? 'Aucun résultat' : 'Saisissez pour rechercher'}
            loadingText="Recherche…"
            renderInput={params => (
                <TextField
                    {...params}
                    placeholder={selected.length ? undefined : placeholder}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading && <CircularProgress size={16} />}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}

export default RemoteSearchEditor;
